import { controlEventTemplateApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  ControlEventTemplateListI,
  ControlEventTemplateI,
  ControlEventTemplateOptionI,
  GetControlEventTemplateParamsI,
} from "@interfaces";
import { AxiosError } from "axios";

const getControlEventTemplates = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
}: GetControlEventTemplateParamsI): Promise<
  ServiceResponseI<DataI<ControlEventTemplateListI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());

    const { data } = await controlEventTemplateApi.get<
      ServiceResponseI<DataI<ControlEventTemplateListI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllControlEventTemplates = async (): Promise<
  ServiceResponseI<ControlEventTemplateOptionI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventTemplateApi.get<
      ServiceResponseI<ControlEventTemplateOptionI[]>
    >("/all-no-pagination", { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventTemplateById = async (
  id: string,
): Promise<ServiceResponseI<ControlEventTemplateI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventTemplateApi.get<
      ServiceResponseI<ControlEventTemplateI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventTemplatePost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventTemplateApi.post<
      ServiceResponseI<string>
    >("", payload, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventTemplatePut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventTemplateApi.put<
      ServiceResponseI<string>
    >(`/${id}`, payload, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventTemplateDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventTemplateApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useControlEventTemplates = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
}: GetControlEventTemplateParamsI) => {
  const controlEventTemplatesQuery = useQuery(
    [
      "control-event-templates",
      { pagination, sorting, globalFilter, clientId },
    ],
    () =>
      getControlEventTemplates({ pagination, sorting, globalFilter, clientId }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { controlEventTemplatesQuery };
};
